import STableLayout from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { AddIQCItem, AddIQCOrder, IQCOrder, IQCResEnum, OrderV2, QCStateEnum, QCTypeEnum } from "@/types/inventoryV2";
import { Flex, Form, Input, InputNumber, Select, TableProps, Tabs, TabsProps, Typography, Descriptions } from "antd";
import { FC, RefObject, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SDoc from "@/components/SDoc";
import { AnyObject } from "antd/es/_util/type";
import IconFolder from "@/assets/images/fileIcon/folder.svg";
import { Product } from "@/types/product";
import { numFormmater } from "@/pages/Requirement/util";
import useIqcEnums from "../../../hooks/useIqcEnums";
import { SelectUsers } from "@/components/Selector";
import { User } from "@/types/user";
import { ChangeEnum } from "@/types/inventory";
import { DescriptionsItemType } from "antd/es/descriptions";
import { dateFormat } from "@/utils/format";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import { qcItemUpdate } from "@/apis/inventoryv2";

type DataType = AddIQCItem & { id: string, product: Product, count: number, inspector: User | null };
export type SubmitItem = AddIQCItem & { id: string };
export type SubmitData = Omit<AddIQCOrder, "items"> & { items: Array<SubmitItem> }
export interface AddIQCFormRef {
  getSubmitData: () => SubmitData;
};

interface AddIQCFormProps {
  order: OrderV2;
  qcOrder?: IQCOrder;
  formType: "ADD" | "EDIT";
  formRef: RefObject<AddIQCFormRef>;
  readonly?: boolean;
};

const AddIQCForm: FC<AddIQCFormProps> = ({ order, qcOrder, formType, formRef, readonly = false }) => {
  const { t } = useTranslation();
  const { IQCResEnumList, IQCDefectEnumList, IQCDisposeEnumList, QCTypeEnumList, getQCType, getQCState } = useIqcEnums();
  const [itemsData, setItemsData] = useState<DataType[]>([]);

  const [activeKey, setActiveKey] = useState<string>("list");
  const [openDocItem, setOpenDocItem] = useState<DataType | null>(null);
  const [desc, setDesc] = useState<string>("");
  const [qcType, setQcType] = useState<QCTypeEnum>(QCTypeEnum.INBOUND);

  const { curUser } = useSelector((state: RootState) => state.userReducer);

  useImperativeHandle(formRef, () => ({
    getSubmitData: () => ({
      qcType,
      sourceOrderId: order.id,
      desc,
      items: itemsData.map(item => ({
        id: item.id,
        productId: item.productId,
        lot: item.lot,
        checkCount: item.checkCount,
        passCount: item.passCount,
        failCount: item.failCount,
        qcResult: item.qcResult,
        defectType: item.defectType,
        disposition: item.disposition,
        inspectorId: item.inspectorId,
        remarks: item.remarks,
        docId: item.docId,
      }))
    })
  }));

  useEffect(() => {
    if (formType === "ADD") initAdd();
    if (formType === "EDIT" && !!qcOrder) initDetail();
  }, [order, qcOrder, formType]);

  const initAdd = () => {
    if (order.items?.length) {
      let res: DataType[] = [];
      order.items.forEach(item => {
        res.push({
          id: item.id,
          productId: item.productId,
          product: item.product,
          lot: item.lot,
          count: item.count,
          checkCount: 0,
          passCount: 0,
          failCount: 0,
          qcResult: IQCResEnum.NOT_IQC,
          defectType: null,
          disposition: null,
          inspectorId: curUser.id,
          remarks: '',
          docId: '',
          inspector: null
        })
      })
      setItemsData([...res]);
    }
    [ChangeEnum.PRE_IN, ChangeEnum.IN].includes(order.type) && setQcType(QCTypeEnum.INBOUND);
    [ChangeEnum.PRE_OUT, ChangeEnum.OUT].includes(order.type) && setQcType(QCTypeEnum.OUTBOUND);
  };

  const initDetail = () => {
    if (order.items?.length && qcOrder?.items?.length) {
      let res: DataType[] = [];
      qcOrder.items.forEach(item => {
        res.push({
          id: item.id,
          productId: item.productId,
          product: item.product,
          lot: item.lot,
          count: order.items.find(oi => oi.productId === item.productId && oi.lot === item.lot)?.count || 0,
          checkCount: item.checkCount,
          passCount: item.passCount,
          failCount: item.failCount,
          qcResult: item.qcResult,
          defectType: item.defectType,
          disposition: item.disposition,
          inspectorId: item.inspectorId,
          remarks: item.remarks,
          docId: item.docId,
          inspector: item.inspector,
        })
      });
      setQcType(qcOrder.qcType);
      setDesc(qcOrder.desc);
      setItemsData([...res]);
    }
  };

  const handelNewDocId = (docId: string, itemId: string) => {
    if (openDocItem && docId && formType === "EDIT") {
      let data: AddIQCItem = {
        lot: openDocItem.lot,
        productId: openDocItem.productId,
        checkCount: openDocItem.checkCount,
        passCount: openDocItem.passCount,
        failCount: openDocItem.failCount,
        qcResult: openDocItem.qcResult,
        defectType: openDocItem.defectType,
        disposition: openDocItem.disposition,
        inspectorId: openDocItem.inspectorId,
        remarks: openDocItem.remarks,
        docId,
      }
      qcItemUpdate(itemId, data);
    }
    setItemsData(last => last.map(item => ({ ...item, docId: item.id === itemId ? docId : item.docId })))
  }

  const handleOpenDoc = (item: DataType) => {
    setActiveKey("doc");
    setOpenDocItem({ ...item });
  };

  const hanldeClose = (e: React.MouseEvent | React.KeyboardEvent | string, action: "add" | "remove") => {
    if (action === "remove") {
      setOpenDocItem(null);
      setActiveKey("list");
    }
  }

  const editItem = <K extends keyof DataType>(itemId: string, key: K, value: DataType[K]) => {
    if (readonly) return
    setItemsData(last => last.map(item => ({
      ...item,
      [key]: itemId === item.id ? value : item[key]
    })))
  }

  const columns: TableProps<DataType>['columns'] = [
    {
      title: <SText text={t("counting.lotStr")} />,
      dataIndex: "lot",
      width: 280,
      render: (_, item) => <SText text={item?.lot ?? ''} />,
    },
    {
      title: <SText text={t("product.code")} />,
      dataIndex: "code",
      width: 100,
      render: (_, item) => <SText text={item?.product?.code ?? ''} />,
    },
    {
      title: <SText text={t("product.name")} />,
      dataIndex: "name",
      width: 100,
      render: (_, item) => <SText text={item?.product?.name ?? ''} />,
    },
    {
      title: <SText text={t("purchase.printModel")} />,
      dataIndex: "model1",
      width: 120,
      render: (_, item) => <SText text={item?.product?.model ?? ''} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      dataIndex: "uom",
      width: 80,
      render: (_, item) => <SText text={item?.product?.uom ?? ''} />,
    },
    {
      title: <SText text={t("warehouse.itemCount")} />,
      dataIndex: "count",
      width: 100,
      render: (_, item) => item.count || 0,
    },
    {
      title: <SText text={t("warehouse.checkCount")} />,
      dataIndex: "checkCount",
      width: 100,
      render: (_, item) => <InputNumber changeOnWheel
        readOnly={readonly}
        value={item.checkCount}
        formatter={val => numFormmater(Number(val || ''))}
        min={0}
        max={item.count}
        step={1}
        precision={0}
        placeholder={t("warehouse.checkCountPl")}
        style={{ width: "100%" }}
        onChange={val => editItem(item.id, "checkCount", val || 0)}
      />,
    },
    {
      title: <SText text={t("warehouse.passCount")} />,
      dataIndex: "passCount",
      hidden: formType === "ADD",
      width: 100,
      render: (_, item) => <InputNumber changeOnWheel
        readOnly={readonly}
        value={item.passCount}
        formatter={val => numFormmater(Number(val || ''))}
        min={0}
        max={item.count}
        step={1}
        precision={0}
        placeholder={t("warehouse.passCounPl")}
        style={{ width: "100%" }}
        onChange={val => editItem(item.id, "passCount", val || 0)}
      />,
    },
    {
      title: <SText text={t("warehouse.failCount")} />,
      dataIndex: "failCount",
      hidden: formType === "ADD",
      width: 100,
      render: (_, item) => <InputNumber changeOnWheel
        readOnly={readonly}
        value={item.failCount}
        formatter={val => numFormmater(Number(val || ''))}
        min={0}
        max={item.count}
        step={1}
        precision={0}
        placeholder={t("warehouse.failCountPl")}
        style={{ width: "100%" }}
        onChange={val => editItem(item.id, "failCount", val || 0)}
      />,
    },
    {
      title: <SText text={t("warehouse.qcResult")} />,
      dataIndex: "qcResult",
      hidden: formType === "ADD",
      render: (_, item) => <Select
        disabled={readonly}
        value={item.qcResult}
        options={IQCResEnumList}
        onChange={val => editItem(item.id, "qcResult", val)}
        style={{ width: "100%" }}
        placeholder={t('warehouse.qcResultPl')}
      />
    },
    {
      title: <SText text={t("warehouse.defectType")} />,
      dataIndex: "defectType",
      hidden: formType === "ADD",
      render: (_, item) => <Select
        disabled={readonly}
        value={item.defectType}
        options={IQCDefectEnumList}
        onChange={val => editItem(item.id, "defectType", val)}
        style={{ width: "100%" }}
        placeholder={t('warehouse.defectTypePl')}
      />
    },
    {
      title: <SText text={t("warehouse.disposition")} />,
      dataIndex: "disposition",
      hidden: formType === "ADD",
      render: (_, item) => <Select
        disabled={readonly}
        value={item.disposition}
        options={IQCDisposeEnumList}
        onChange={val => editItem(item.id, "disposition", val)}
        style={{ width: "100%" }}
        placeholder={t('warehouse.dispositionPl')}
      />
    },
    {
      title: <SText text={t("warehouse.iqcUser")} />,
      dataIndex: "inspectorId",
      width: 200,
      render: (_, item) => <SelectUsers
        disabled={readonly}
        defaultList={formType === "EDIT" ? (item.inspector ? [item.inspector] : []) : [curUser]}
        value={!!item.inspectorId ? [item.inspectorId] : []}
        onChange={val => editItem(item.id, "inspectorId", val[0])}
        width="100%"
        maxCount={1}
        getPopupContainer={() => document.body}
      />
    },
    {
      title: <SText text={t("warehouse.remarks")} />,
      dataIndex: "remarks",
      width: 180,
      render: (_, item) => <Input.TextArea
        readOnly={readonly}
        value={item.remarks}
        onChange={e => editItem(item.id, "remarks", e.target.value)}
        placeholder={t('warehouse.itemDesc')}
        autoSize={{ minRows: 2, maxRows: 2 }}
      />
    },
    {
      title: t("common.attachment"),
      key: "docId",
      fixed: 'right',
      width: 100,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={IconFolder}
            alt=""
            style={{ cursor: "pointer" }}
            onClick={() => handleOpenDoc(item)}
          />
        </Flex>
      )
    }
  ];

  const tabs: TabsProps["items"] = useMemo(() => {
    let res: TabsProps["items"] = [
      {
        key: "list",
        label: t("warehouse.qcItemList"),
        closable: false
      }
    ];
    if (!!openDocItem) res.push(
      {
        key: "doc",
        label: (openDocItem?.product.code) + ' ' + t('warehouse.doc'),
      }
    )
    return res
  }, [openDocItem]);

  const descriptionItems: DescriptionsItemType[] = useMemo(() => {
    if (formType === "ADD") {
      return (
        [
          {
            key: "sourceOrder",
            label: t('warehouse.iqcInventory'),
            children: <Typography.Text>{order.number}</Typography.Text>
          },
          {
            key: "createdAt",
            label: t('warehouse.createdAt'),
            span: 1,
            children: dateFormat(dayjs(Date.now()), true)
          },
          {
            key: "creator",
            label: t('warehouse.creator'),
            span: 1,
            children: (
              curUser && <>
                <WMSAvatar creator={curUser} uniChar={curUser.id}
                  avatarId={curUser.avatar}
                  size={24} alt={getUserAvatarTip(curUser)}
                />
                <span>{getUserName(curUser)}</span>
              </>
            )
          },
          {
            key: "qcType",
            label: t('warehouse.iqcType'),
            span: 1,
            children: <Select
              value={qcType}
              onChange={setQcType}
              options={QCTypeEnumList}
              placeholder={t('warehouse.qcTyePl')}
              style={{ width: "100%" }}
            />
          },
          // {
          //   key: "desc",
          //   label: t('warehouse.desc'),
          //   span: 2,
          //   children: <Input
          //     placeholder={t('warehouse.descPl')}
          //     value={desc}
          //     maxLength={1000}
          //     onChange={e => setDesc(e.target.value)}
          //   />
          // },
        ]
      )
    } else {
      const tagInfo = {
        typeInfo: getQCType(qcOrder?.qcType ?? null),
        stateInfo: getQCState(qcOrder?.qcState ?? null),
      };
      return (
        !!qcOrder ? [
          {
            key: "sourceOrder",
            label: t('warehouse.iqcInventory'),
            children: <Typography.Text>{order.number}</Typography.Text>
          },
          {
            key: "createdAt",
            label: t('warehouse.createdAt'),
            span: 1,
            children: qcOrder.createdAt && dateFormat(dayjs(qcOrder.createdAt), true)
          },
          {
            key: "creator",
            label: t('warehouse.creator'),
            span: 1,
            children: qcOrder.creator && (
              <>
                <WMSAvatar creator={qcOrder.creator} uniChar={qcOrder.createdBy}
                  avatarId={qcOrder.creator.avatar}
                  size={24} alt={qcOrder?.creator ? getUserAvatarTip(qcOrder?.creator) : ''}
                />
                <span>{getUserName(qcOrder.creator)}</span>
              </>
            )
          },
          {
            key: "qcType",
            label: t('warehouse.iqcType'),
            span: 1,
            children: tagInfo.typeInfo && <SText tagMode text={tagInfo.typeInfo.label} color={tagInfo.typeInfo.color} />
          },
          {
            key: "inspectedAt",
            label: t('warehouse.iqcTime'),
            span: 1,
            children: qcOrder.inspectedAt && dateFormat(dayjs(qcOrder.inspectedAt), true)
          },
          // {
          //   key: "inspector",
          //   label: t('warehouse.iqcUser'),
          //   span: 1,
          //   children: qcOrder.inspector && (
          //     <>
          //       <WMSAvatar creator={qcOrder.inspector} uniChar={qcOrder.inspectorId}
          //         avatarId={qcOrder.inspector.avatar}
          //         size={24} alt={qcOrder?.inspector ? getUserAvatarTip(qcOrder?.inspector) : ''}
          //       />
          //       <span>{getUserName(qcOrder.inspector)}</span>
          //     </>
          //   )
          // }
        ] : []
      )
    }
  }, [order, qcOrder, formType, curUser, desc, qcType])

  return (
    <Flex vertical gap={4} style={{ width: "100%", height: "100%" }} >
      <Descriptions bordered items={descriptionItems} column={3} style={{ marginTop: 6 }} />
      <Typography.Title level={4} style={{ margin: "4px 0" }}>{t('warehouse.qcItems')}</Typography.Title>
      <Tabs
        onEdit={hanldeClose}
        activeKey={activeKey}
        onChange={setActiveKey}
        type="editable-card"
        items={tabs}
        hideAdd
      />
      <div className="content">
        {
          activeKey === "list" ? (
            <STableLayout
              dataSource={itemsData}
              columns={columns as Array<AnyObject>}
              headerChildren={null}
              extHeight={70}
              pagination={false}
              total={itemsData.length}
              scrollX={1000}
            />
          ) : activeKey === "doc" ? (
            <SDoc docId={openDocItem?.docId} handleNewDocId={id => handelNewDocId(id, openDocItem?.id ?? '')} isRead={readonly} />
          ) : null
        }
      </div>
    </Flex>
  )
};

export default AddIQCForm;